
import { useMemo, useEffect } from 'react';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useSessionManager } from './hooks/session';
import { useAuthOperations } from './hooks/useAuthOperations';

export function useAuthProvider() {
  const { isOnline } = useNetworkStatus();
  const { user, session, isLoading: sessionLoading, setUser, setSession, checkSession } = useSessionManager();
  const { isLoading: authLoading, mfaActions, login, loginWithProvider, logout, signup, updateUserProfile } = useAuthOperations({ setUser, setSession });
  
  // Initialize session check when component mounts and when online status changes
  useEffect(() => {
    if (isOnline) {
      console.log('Checking session due to online status change');
      checkSession();
    }
  }, [isOnline, checkSession]);
  
  // Memoize the auth context value to prevent unnecessary re-renders
  const authContextValue = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading: sessionLoading || authLoading || mfaActions.isLoading,
    isMfaRequired: mfaActions.isMfaRequired,
    isOnline,
    checkSession, // Expose the checkSession method
    login,
    loginWithProvider,
    logout,
    signup,
    updateUserProfile, // Expose the updateUserProfile method
    verifyMfaOtp: mfaActions.verifyMfaOtp,
    enrollMfa: mfaActions.enrollMfa,
    verifyMfaEnrollment: mfaActions.verifyMfaEnrollment,
    unenrollMfa: mfaActions.unenrollMfa,
    recoverMfaAccount: mfaActions.recoverMfaAccount
  }), [
    user, 
    sessionLoading, 
    authLoading, 
    mfaActions.isLoading, 
    mfaActions.isMfaRequired, 
    isOnline,
    checkSession,
    login, 
    loginWithProvider, 
    logout, 
    signup,
    updateUserProfile, // Add updateUserProfile to dependencies
    mfaActions.verifyMfaOtp, 
    mfaActions.enrollMfa, 
    mfaActions.verifyMfaEnrollment, 
    mfaActions.unenrollMfa,
    mfaActions.recoverMfaAccount
  ]);
  
  return authContextValue;
}
